import { C2S, S2C } from '@shared/protocol.js';
import type { LiveStroke, Op, PeerCursor, PeerViewport } from '@/canvas/types';
import type { Pad } from './api';
import { socketUrl } from './config';

export interface WelcomeMessage {
  t: string;
  connectionId: string;
  role: 'host' | 'guest' | 'spectator';
  pad: Pad;
  peers: Array<{
    connectionId: string;
    user: { id: string; username: string; avatarColor: string };
    role: 'host' | 'guest' | 'spectator';
    cursor: PeerCursor | null;
    viewport: PeerViewport | null;
  }>;
  ops: Op[];
  lastOpSeq: number;
  chat: ChatMessage[];
  liveStrokes: LiveStroke[];
}

export interface ChatMessage {
  id: string;
  userId: string;
  username: string;
  color: string;
  body: string;
  at: number;
}

export type ConnectionState = 'connecting' | 'open' | 'reconnecting' | 'closed';

/** What a clear wipes. Mirrors CLEAR_SCOPES in the shared protocol. */
export type ClearScope = 'mine' | 'mine-drawing' | 'mine-images' | 'all';

type Handler = (msg: any) => void;

/**
 * WebSocket wrapper for one pad session.
 *
 * Two details matter for how live the drawing feels:
 *  - outbound stroke points are coalesced into one message per animation frame,
 *    so a fast pointer produces ~60 small messages a second instead of 300;
 *  - cursor and viewport updates are throttled separately and are always safe
 *    to drop, since only the latest value has any meaning.
 */
export class PadConnection {
  private socket: WebSocket | null = null;

  private handlers = new Map<string, Set<Handler>>();

  private pendingPoints: number[] = [];

  private pendingStrokeId: string | null = null;

  private flushScheduled = false;

  private pendingDraft: unknown | null = null;

  private draftScheduled = false;

  private queue: string[] = [];

  private reconnectAttempts = 0;

  /** Consecutive closes caused by a rejected ticket. */
  private ticketFailures = 0;

  /** When the server last answered a ping, for spotting a socket that died while asleep. */
  private lastPongAt = 0;

  private wakeBound = false;

  private reconnectTimer: number | null = null;

  private closedByUs = false;

  private lastCursorSent = 0;

  private lastViewportSent = 0;

  private pingTimer: number | null = null;

  /** Highest op sequence we have seen; used to resync after a reconnect. */
  lastOpSeq = 0;

  state: ConnectionState = 'connecting';

  latency = 0;

  /**
   * @param ticket    the ticket the pad was opened with
   * @param onState   connection-state changes, for the UI banner
   * @param refresh   mints a replacement ticket. Pad tickets expire after two
   *   minutes, so every reconnect after that — which is every laptop that has
   *   been asleep — needs a new one. Reusing the original is what produced
   *   "your invite expired" the moment a machine woke up.
   */
  constructor(
    private ticket: string,
    private onState: (s: ConnectionState) => void,
    private refresh?: () => Promise<string>,
  ) {}

  async connect() {
    this.closedByUs = false;
    this.watchWake();
    this.setState(this.reconnectAttempts ? 'reconnecting' : 'connecting');

    // The ticket we were handed is good for the first connection. After that
    // it has almost certainly expired, so get another before dialling.
    if (this.reconnectAttempts > 0 && this.refresh) {
      try {
        this.ticket = await this.refresh();
      } catch {
        // Offline, or the pad is gone. Fall through and let the socket fail;
        // onclose schedules the next attempt with backoff.
      }
      if (this.closedByUs) return;
    }

    const socket = new WebSocket(socketUrl());
    this.socket = socket;

    socket.onopen = () => {
      this.reconnectAttempts = 0;
      this.setState('open');
      // `since` asks the server for only the ops we missed while away.
      this.sendNow({ t: C2S.HELLO, ticket: this.ticket, since: this.lastOpSeq });
      for (const item of this.queue.splice(0)) socket.send(item);
      this.startPing();
    };

    socket.onmessage = (event) => {
      let msg: any;
      try {
        msg = JSON.parse(event.data);
      } catch {
        return;
      }
      if (msg.t === S2C.PONG) {
        this.latency = Math.round((Date.now() - msg.at) / 2);
        this.lastPongAt = Date.now();
        return;
      }
      if (msg.t === S2C.WELCOME) this.lastOpSeq = Math.max(this.lastOpSeq, msg.lastOpSeq ?? 0);
      if (msg.op?.seq) this.lastOpSeq = Math.max(this.lastOpSeq, msg.op.seq);
      this.emit(msg.t, msg);
      this.emit('*', msg);
    };

    socket.onclose = (event) => {
      this.stopPing();
      if (this.closedByUs) {
        this.setState('closed');
        return;
      }
      // Full and kicked are "do not come back".
      if ([4005, 4008].includes(event.code)) {
        this.setState('closed');
        this.emit('fatal', { code: event.code, reason: event.reason });
        return;
      }
      // A rejected ticket is usually just an expired one. Mint another and try
      // again; only give up once fresh tickets keep being refused, which means
      // the session or the pad has really gone.
      if (event.code === 4003) {
        this.ticketFailures += 1;
        if (!this.refresh || this.ticketFailures > 3) {
          this.setState('closed');
          this.emit('fatal', { code: event.code, reason: event.reason });
          return;
        }
      } else {
        this.ticketFailures = 0;
      }
      this.scheduleReconnect();
    };

    socket.onerror = () => { /* onclose does the work */ };
  }

  private setState(state: ConnectionState) {
    if (this.state === state) return;
    this.state = state;
    this.onState(state);
  }

  private scheduleReconnect() {
    this.setState('reconnecting');
    this.reconnectAttempts += 1;
    // Exponential backoff with jitter, capped at 15s.
    const delay = Math.min(15_000, 400 * 2 ** Math.min(this.reconnectAttempts, 6))
      * (0.7 + Math.random() * 0.6);
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
  }

  private startPing() {
    this.stopPing();
    this.pingTimer = window.setInterval(() => this.sendNow({ t: C2S.PING, at: Date.now() }), 5000);
  }

  private stopPing() {
    if (this.pingTimer) window.clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  on(type: string, handler: Handler) {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    set.add(handler);
    return () => set!.delete(handler);
  }

  private emit(type: string, msg: any) {
    const set = this.handlers.get(type);
    if (set) for (const fn of set) fn(msg);
  }

  private sendNow(msg: unknown) {
    const data = JSON.stringify(msg);
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(data);
  }

  /** Send, or queue until we are connected again. */
  send(msg: unknown) {
    const data = JSON.stringify(msg);
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(data);
    } else if (this.queue.length < 500) {
      this.queue.push(data);
    }
  }

  // --- stroke streaming ---------------------------------------------------

  beginStroke(strokeId: string, style: unknown, points: number[]) {
    this.flushPoints();
    this.pendingStrokeId = strokeId;
    this.send({ t: C2S.STROKE_BEGIN, strokeId, style, points });
  }

  /** Buffer points; they go out on the next frame as a single message. */
  pushPoints(strokeId: string, points: number[]) {
    if (this.pendingStrokeId !== strokeId) {
      this.flushPoints();
      this.pendingStrokeId = strokeId;
    }
    this.pendingPoints.push(...points);
    if (!this.flushScheduled) {
      this.flushScheduled = true;
      requestAnimationFrame(() => {
        this.flushScheduled = false;
        this.flushPoints();
      });
    }
  }

  private flushPoints() {
    if (!this.pendingStrokeId || !this.pendingPoints.length) return;
    this.send({ t: C2S.STROKE_POINTS, strokeId: this.pendingStrokeId, points: this.pendingPoints });
    this.pendingPoints = [];
  }

  endStroke(strokeId: string, op: Op | null) {
    this.flushPoints();
    this.pendingStrokeId = null;
    this.send({ t: C2S.STROKE_END, strokeId, op });
  }

  commitOp(op: Op) {
    this.send({ t: C2S.OP, op });
  }

  /**
   * Move or resize an existing op. `preview` messages are relayed to peers but
   * not written to disk, so a drag streams smoothly and only the final
   * position costs a database write.
   */
  updateOp(id: string, patch: Record<string, unknown>, preview: boolean) {
    this.send({ t: C2S.OP_UPDATE, id, patch, preview });
  }

  deleteOps(ids: string[]) {
    if (ids.length) this.send({ t: C2S.OP_DELETE, ids });
  }

  restoreOps(ids: string[]) {
    if (ids.length) this.send({ t: C2S.OP_RESTORE, ids });
  }

  clear(scope: ClearScope) {
    this.send({ t: C2S.CLEAR, scope });
  }

  /** Throttled to ~30Hz — only the newest position is ever interesting. */
  /**
   * `force` skips the throttle. Presses and releases have to go out even if
   * one just did, because they are events rather than samples — dropping one
   * leaves a peer's cursor stuck in the wrong state.
   */
  sendCursor(cursor: PeerCursor | null, force = false) {
    const now = performance.now();
    if (!force && now - this.lastCursorSent < 33) return;
    this.lastCursorSent = now;
    this.send({ t: C2S.CURSOR, cursor });
  }

  /**
   * A shape being dragged out. Coalesced to one message per frame like stroke
   * points, and never persisted — peers draw it from this and then get the
   * real op when it is committed.
   */
  sendDraft(op: unknown | null) {
    if (op === null) {
      this.pendingDraft = null;
      this.send({ t: C2S.DRAFT, op: null });
      return;
    }
    this.pendingDraft = op;
    if (this.draftScheduled) return;
    this.draftScheduled = true;
    requestAnimationFrame(() => {
      this.draftScheduled = false;
      if (this.pendingDraft) this.send({ t: C2S.DRAFT, op: this.pendingDraft });
    });
  }

  /**
   * Throttled to ~8Hz; presence rectangles do not need to be frame-perfect.
   * `force` bypasses the throttle for the periodic heartbeat, which is what
   * tells peers where you are when you have not moved at all.
   */
  sendViewport(viewport: PeerViewport, force = false) {
    const now = performance.now();
    if (!force && now - this.lastViewportSent < 120) return;
    this.lastViewportSent = now;
    this.send({ t: C2S.VIEWPORT, viewport });
  }

  sendLaser(points: number[]) {
    this.send({ t: C2S.LASER, points });
  }

  sendChat(body: string) {
    this.send({ t: C2S.CHAT, body });
  }

  updatePad(fields: { title?: string; settings?: Record<string, unknown> }) {
    this.send({ t: C2S.PAD_UPDATE, ...fields });
  }

  kick(userId: string) {
    this.send({ t: C2S.KICK, userId });
  }

  /**
   * A machine coming out of sleep, or a network coming back, should not sit
   * out the remaining backoff — and a socket that looks open after a sleep is
   * often already dead, because nothing was running to notice it drop. Check
   * both the moment the page is in front of someone again.
   */
  private onWake = () => {
    if (this.closedByUs) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

    if (this.state === 'open') {
      // Pings go out every 5s. Nothing back for 20s means the socket is stale
      // even though the browser has not worked that out yet.
      if (this.lastPongAt && Date.now() - this.lastPongAt > 20_000) {
        try { this.socket?.close(4900, 'stale'); } catch { /* already gone */ }
      }
      return;
    }

    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    // Keep it non-zero so connect() still refreshes the ticket.
    this.reconnectAttempts = Math.max(1, Math.min(this.reconnectAttempts, 2));
    void this.connect();
  };

  private watchWake() {
    if (typeof window === 'undefined' || this.wakeBound) return;
    this.wakeBound = true;
    document.addEventListener('visibilitychange', this.onWake);
    window.addEventListener('online', this.onWake);
    window.addEventListener('focus', this.onWake);
  }

  private unwatchWake() {
    if (!this.wakeBound) return;
    this.wakeBound = false;
    document.removeEventListener('visibilitychange', this.onWake);
    window.removeEventListener('online', this.onWake);
    window.removeEventListener('focus', this.onWake);
  }

  close() {
    this.closedByUs = true;
    this.stopPing();
    this.unwatchWake();
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    try { this.socket?.close(1000, 'leaving'); } catch { /* already gone */ }
    this.socket = null;
    this.handlers.clear();
    this.setState('closed');
  }
}
